<template>
  <div
    class="relative rounded-2xl hover:shadow-2xl border-slate-200 my-4 h-full flex flex-col sm:max-w-3xl podcast-card-full overflow-hidden"
  >
    <div class="flex flex-col items-end gap-6 podcast-card-content">
      <img
        :src="audio.thumbnail_url"
        :alt="audio.title"
        class="w-full object-cover podcast-thumbnail"
      />
      <div class="absolute top-2 left-4">
        <h1 class="text-lg font-bold text-white uppercase mt-2 podcast-title">
          {{ audio.title }}
        </h1>
      </div>
      <div
        class="absolute bottom-0 left-0 bg-black/50 flex flex-col pt-2 pb-3 px-2 podcast-info w-full"
      >
        <div class="flex flex-col sm:flex-row w-full items-end player-controls">
          <div
            class="flex flex-row gap-2 items-center w-full px-2 py-2 rounded-lg audio-player flex-1"
          >
            <button @click="backToStart" class="mr-1 rewind-button">
              <img
                class="w-8 rewind-icon"
                src="~/assets/img/Rewind.svg"
                alt="Back to Start"
              />
            </button>
            <button @click="togglePlay" class="play-button">
              <img class="w-16 play-icon" :src="playPauseIcon" alt="Play/Pause" />
            </button>
            <div class="time-display">
              <p class="font-medium text-white text-xs px-3 py-1 current-time">
                {{ currentTime }}
              </p>
            </div>
            <div class="flex flex-col w-full progress-container">
              <div
                class="w-full h-2 bg-slate-400 rounded-full cursor-pointer progress-bar"
                @click="handleProgressClick"
                ref="progressBar"
              >
                <div
                  :style="{ width: progress + '%' }"
                  class="h-2 bg-brand rounded-full progress-fill"
                ></div>
              </div>
            </div>
            <div class="time-display">
              <p class="font-medium text-white text-xs px-3 py-1 duration-time">
                {{ duration }}
              </p>
            </div>
          </div>
          <div class="flex gap-2 items-center justify-between action-buttons">
            <button
              v-if="audio.meditationTimestamp"
              @click="skipToMeditation"
              class="text-nowrap self-start bg-brand text-[10px] text-white rounded-full px-4 py-1 font-medium hover:bg-opacity-80 transition-colors flex items-center gap-1 meditation-button"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="hidden h-6 w-6 meditation-icon"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                  clip-rule="evenodd"
                />
              </svg>
              Skip to Meditation
            </button>
            <button
              @click="toggleContent"
              class="text-white text-[10px] font-medium rounded-full px-2 py-1 hover:bg-brand/20 transition-colors flex items-center gap-1 toggle-content-button"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 transition-transform"
                :class="{ 'rotate-180': isContentExpanded }"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
        </div>
        <div
          id="podcast-card-content"
          v-show="isContentExpanded"
          class="transition-all duration-300 mt-2 px-3 pb-2"
          :class="{
            'max-h-0 opacity-0 invisible': !isContentExpanded,
            'max-h-screen opacity-100 visible': isContentExpanded
          }"
        >
          <div
            class="flex flex-col gap-2 items-center justify-between w-full pb-3 border-b border-slate-200 podcast-header"
          >
            <div class="w-full">
              <div
                class="flex flex-row justify-between mb-1 py-2 gap-2 items-center episode-info"
              >
                <div
                  class="text-white border border-white rounded-full text-[10px] px-3 py-1 episode-number"
                >
                  <p>{{ audio.episode_number }}</p>
                </div>
                <div
                  class="flex flex-row gap-2 items-center font-medium text-white text-[10px] px-3 py-1 episode-date"
                >
                  <img src="~/assets/img/Calendar.svg" alt="Calendar" />
                  <p>
                    {{
                      new Date(audio.created_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "2-digit",
                        year: "numeric"
                      })
                    }}
                  </p>
                </div>
              </div>
              <div class="flex flex-row gap-2 flex-wrap justify-end categories">
                <div
                  v-for="(cat, index) in audio.categories"
                  :key="index"
                  class="text-white border border-white rounded-full text-xs px-3 py-1 category-tag"
                >
                  <p>{{ cat }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="flex flex-col w-full podcast-details">
            <h1 class="text-lg font-bold text-white uppercase mt-2 podcast-title">
              {{ audio.title }}
            </h1>
            <h2 class="text-white font-medium text-md podcast-subtitle">
              {{ audio.subtitle }}
            </h2>

            <div class="w-full mt-3 podcast-description-container">
              <div
                @click="toggleDescription"
                class="relative cursor-pointer description-toggle"
              >
                <p
                  class="text-slate-200 text-sm pr-6 text-justify podcast-description"
                  :class="{
                    'line-clamp-2': !isDescriptionExpanded,
                    'description-collapsed': !isDescriptionExpanded
                  }"
                >
                  {{ audio.description }}
                </p>
                <div class="absolute top-0 right-0 text-white toggle-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 transition-transform toggle-arrow"
                    :class="{
                      'rotate-180': isDescriptionExpanded,
                      'toggle-arrow-expanded': isDescriptionExpanded
                    }"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="audioError" class="mt-4 p-2 bg-red-100 text-red-700 rounded error-message">
      Error: {{ audioError }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
// Import the required images
import downloadIcon from "~/assets/img/Download.svg";
import shareIcon from "~/assets/img/Share.svg";
import expandIcon from "~/assets/img/Expand screen.svg";
import playIcon from "~/assets/img/play=true.svg";
import pauseIcon from "~/assets/img/play=false.svg";
import rewindIcon from "~/assets/img/Rewind.svg";

const props = defineProps({
  audio: {
    type: Object,
    required: true
  },
  audioSrc: {
    type: String,
    required: true
  }
});

// Computed property to handle backward compatibility

const isPlaying = ref(false);
const progress = ref(0);
const currentTime = ref("00:00");
const duration = ref("00:00");
const lastPlaybackPosition = ref(0); // Store the last playback position
const playPauseIcon = computed(() => (isPlaying.value ? pauseIcon : playIcon));
const audioElement = ref(null);
const audioError = ref(null);
const progressBar = ref(null);
const isDescriptionExpanded = ref(false);
const isContentExpanded = ref(false); // Add this for accordion functionality

// Toggle content expansion
const toggleContent = () => {
  isContentExpanded.value = !isContentExpanded.value;
};

// Toggle description expansion
const toggleDescription = () => {
  isDescriptionExpanded.value = !isDescriptionExpanded.value;
};

// Format seconds to MM:SS format
const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return "00:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

// Convert timestamp string (MM:SS) to seconds
const timestampToSeconds = (timestamp) => {
  if (!timestamp || timestamp === "") return 0;

  const parts = timestamp.split(":");
  if (parts.length !== 2) return 0;

  const minutes = parseInt(parts[0], 10);
  const seconds = parseInt(parts[1], 10);

  if (isNaN(minutes) || isNaN(seconds)) return 0;

  return minutes * 60 + seconds;
};

// Skip to meditation timestamp
const skipToMeditation = () => {
  if (!audioElement.value || !props.audio.meditationTimestamp) return;

  const targetSeconds = timestampToSeconds(props.audio.meditationTimestamp);

  // Check if the audio is loaded and has a valid duration
  if (!audioElement.value.duration) {
    // If duration is not available yet, set up a one-time event listener
    const onceLoaded = () => {
      if (targetSeconds > 0 && targetSeconds < audioElement.value.duration) {
        audioElement.value.currentTime = targetSeconds;

        // Start playing if not already playing
        if (!isPlaying.value) {
          togglePlay();
        }
      }
      audioElement.value.removeEventListener("loadedmetadata", onceLoaded);
    };

    audioElement.value.addEventListener("loadedmetadata", onceLoaded);

    // Try to load the audio
    audioElement.value.load();
    return;
  }

  // If duration is available, skip to the timestamp
  if (targetSeconds > 0 && targetSeconds < audioElement.value.duration) {
    audioElement.value.currentTime = targetSeconds;

    // Start playing if not already playing
    if (!isPlaying.value) {
      togglePlay();
    }
  }
};

// Handle click on progress bar to seek
const handleProgressClick = (event) => {
  if (!audioElement.value || !progressBar.value) return;

  const rect = progressBar.value.getBoundingClientRect();
  const offsetX = event.clientX - rect.left;
  const clickPositionPercentage = offsetX / rect.width;

  // Set current time based on click position
  if (audioElement.value.duration) {
    audioElement.value.currentTime =
      clickPositionPercentage * audioElement.value.duration;
  }
};

// Initialize audio element
onMounted(() => {
  try {
    // Create audio element
    audioElement.value = new Audio();

    // Set up event listeners
    audioElement.value.addEventListener("loadedmetadata", () => {
      console.log("Audio metadata loaded, duration:", audioElement.value.duration);
      duration.value = formatTime(audioElement.value.duration);
    });

    audioElement.value.addEventListener("timeupdate", () => {
      const currentTimeSeconds = audioElement.value.currentTime;
      currentTime.value = formatTime(currentTimeSeconds);

      // Update progress bar
      if (audioElement.value.duration && !isNaN(audioElement.value.duration)) {
        progress.value = (currentTimeSeconds / audioElement.value.duration) * 100;
      }
    });

    audioElement.value.addEventListener("ended", () => {
      isPlaying.value = false;
      audioElement.value.currentTime = 0;
      lastPlaybackPosition.value = 0; // Reset the saved position
      progress.value = 0;
    });

    audioElement.value.addEventListener("error", (e) => {
      console.error("Audio error:", e);
      audioError.value = `Cannot play audio: ${
        e.target.error ? e.target.error.message : "Unknown error"
      }`;
    });

    // Set the source and load the audio
    audioElement.value.src = props.audio.file_url;
    audioElement.value.load();
    console.log("Audio source set to:", props.audioSrc);

    // Preload audio
    audioElement.value.preload = "auto";
  } catch (error) {
    console.error("Error setting up audio:", error);
    audioError.value = `Setup error: ${error.message}`;
  }
});

const togglePlay = () => {
  if (!audioElement.value) return;

  try {
    isPlaying.value = !isPlaying.value;

    if (isPlaying.value) {
      // When resuming, ensure we're at the last position
      if (lastPlaybackPosition.value > 0 && audioElement.value.currentTime === 0) {
        audioElement.value.currentTime = lastPlaybackPosition.value;
      }

      const playPromise = audioElement.value.play();

      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.error("Play error:", error);
          isPlaying.value = false;
          audioError.value = `Play error: ${error.message}`;
        });
      }
    } else {
      // When pausing, store the current position
      lastPlaybackPosition.value = audioElement.value.currentTime;
      audioElement.value.pause();
    }
  } catch (error) {
    console.error("Toggle play error:", error);
    audioError.value = `Play/pause error: ${error.message}`;
  }
};

// Watch for changes to the audioSrc prop and reload if needed
watch(
  () => props.audioSrc,
  (newSrc, oldSrc) => {
    if (newSrc !== oldSrc && audioElement.value) {
      audioElement.value.src = newSrc;
      audioElement.value.load();
      lastPlaybackPosition.value = 0; // Reset the saved position
      progress.value = 0;
      currentTime.value = "00:00";
      isPlaying.value = false;
    }
  },
  { immediate: false }
);

const handleDownload = () => {
  // Create a temporary link to download the file
  const link = document.createElement("a");
  link.href = props.audioSrc;
  link.download = `${props.title.replace(/\s+/g, "-").toLowerCase()}.mp4`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const handleShare = () => {
  // Add share logic here
};

const handleExpand = () => {
  // Add expand logic here
};

// Back to Start function
const backToStart = () => {
  if (!audioElement.value) return;

  audioElement.value.currentTime = 0;
  lastPlaybackPosition.value = 0;

  // Start playing if not already playing
  if (!isPlaying.value) {
    togglePlay();
  }
};
</script>

<style scoped>
/* Custom styling can be added here if needed to override or extend Tailwind */
</style>
